(() => {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const root = document.documentElement;

  const money = (cents, currency = 'USD') => {
    try { return new Intl.NumberFormat(root.lang || 'en', { style: 'currency', currency }).format(cents / 100); }
    catch { return `$${(cents / 100).toFixed(2)}`; }
  };

  /* Theme mode */
  try {
    const savedTheme = localStorage.getItem('swift-vanguard-theme');
    if (savedTheme) root.dataset.theme = savedTheme;
  } catch (_) {}

  qs('[data-theme-toggle]')?.addEventListener('click', () => {
    const next = root.dataset.theme === 'dark' ? 'light' : 'dark';
    root.dataset.theme = next;
    try { localStorage.setItem('swift-vanguard-theme', next); } catch (_) {}
  });

  /* Homepage header overlay */
  const siteHeader = qs('[data-site-header]');
  const updateHeaderState = () => siteHeader?.classList.toggle('is-scrolled', window.scrollY > 24);
  updateHeaderState();
  window.addEventListener('scroll', updateHeaderState, { passive: true });

  /* Homepage hero slideshow */
  qsa('[data-hero-slideshow]').forEach((slideshow) => {
    const slides = qsa('[data-hero-slide]', slideshow);
    const dots = qsa('[data-hero-dot]', slideshow);
    if (slides.length < 2) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const interval = Math.max(3, Number(slideshow.dataset.autoplaySeconds || 6)) * 1000;
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer;

    const showSlide = (requestedIndex) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    const stopAutoplay = () => {
      window.clearInterval(timer);
      timer = undefined;
    };
    const startAutoplay = () => {
      stopAutoplay();
      if (reduceMotion.matches || document.hidden) return;
      timer = window.setInterval(() => showSlide(activeIndex + 1), interval);
    };
    const selectSlide = (index) => {
      showSlide(index);
      startAutoplay();
    };

    dots.forEach((dot) => dot.addEventListener('click', () => selectSlide(Number(dot.dataset.heroDot))));
    qs('[data-hero-previous]', slideshow)?.addEventListener('click', () => selectSlide(activeIndex - 1));
    qs('[data-hero-next]', slideshow)?.addEventListener('click', () => selectSlide(activeIndex + 1));
    document.addEventListener('visibilitychange', startAutoplay);
    reduceMotion.addEventListener?.('change', startAutoplay);
    showSlide(activeIndex);
    startAutoplay();
  });

  /* Navigation */
  const menuButton = qs('[data-mobile-menu-toggle]');
  const mobileNav = qs('[data-mobile-nav]');
  const headerSearch = qs('.header-search');

  const closeNavigation = () => {
    mobileNav?.classList.remove('is-open');
    headerSearch?.classList.remove('is-open');
    menuButton?.setAttribute('aria-expanded', 'false');
    qsa('.nav-dropdown.is-open').forEach((item) => item.classList.remove('is-open'));
  };

  menuButton?.addEventListener('click', () => {
    const open = mobileNav?.classList.toggle('is-open');
    headerSearch?.classList.toggle('is-open', Boolean(open));
    menuButton.setAttribute('aria-expanded', String(Boolean(open)));
  });

  qsa('[data-nav-dropdown]').forEach((button) => {
    button.addEventListener('click', () => {
      const parent = button.closest('.nav-dropdown');
      const open = parent.classList.toggle('is-open');
      button.setAttribute('aria-expanded', String(open));
    });
  });

  mobileNav?.addEventListener('click', (event) => {
    if (event.target.closest('a') && window.innerWidth <= 820) closeNavigation();
  });
  window.addEventListener('resize', () => { if (window.innerWidth > 820) closeNavigation(); });

  /* Predictive search */
  const searchInput = qs('[data-search-input]');
  const searchResults = qs('[data-search-results]');
  let searchTimer;

  searchInput?.addEventListener('input', () => {
    clearTimeout(searchTimer);
    const term = searchInput.value.trim();
    if (term.length < 2) { searchResults?.classList.remove('is-open'); return; }
    searchTimer = setTimeout(async () => {
      try {
        const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,collection&resources[limit]=7`);
        const json = await response.json();
        const data = json.resources?.results || {};
        const products = data.products || [];
        const collections = data.collections || [];
        searchResults.innerHTML = [
          ...products.map((item) => `<a href="${item.url}"><span>${item.title}</span><small>Product</small></a>`),
          ...collections.map((item) => `<a href="${item.url}"><span>${item.title}</span><small>Collection</small></a>`)
        ].join('') || '<a href="#"><span>No matches found</span></a>';
        searchResults.classList.add('is-open');
      } catch (_) { searchResults?.classList.remove('is-open'); }
    }, 220);
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-predictive-search]')) searchResults?.classList.remove('is-open');
  });

  /* Vehicle fitment */
  qs('[data-fitment-form]')?.addEventListener('submit', (event) => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try { localStorage.setItem('swift-vanguard-vehicle', JSON.stringify(values)); } catch (_) {}
    const query = new URLSearchParams();
    if (values.year) query.set('filter.p.m.custom.year', values.year);
    if (values.model) query.set('filter.p.m.custom.model', values.model);
    if (values.trim) query.set('filter.p.m.custom.trim', values.trim);
    window.location.href = `/collections/all?${query}`;
  });

  /* Sale countdown */
  const nextLocalMidnight = () => {
    const target = new Date();
    target.setHours(24, 0, 0, 0);
    return target.getTime();
  };

  const initCountdowns = (context = document) => {
    const countdowns = [
      ...(context.matches?.('[data-countdown]') ? [context] : []),
      ...qsa('[data-countdown]', context)
    ];
    countdowns.forEach((countdown) => {
      if (countdown.dataset.countdownReady === 'true') return;
      let target = nextLocalMidnight();
      const units = {
        hours: qs('[data-countdown-unit="hours"]', countdown),
        minutes: qs('[data-countdown-unit="minutes"]', countdown),
        seconds: qs('[data-countdown-unit="seconds"]', countdown)
      };
      countdown.dataset.countdownReady = 'true';
      const renderCountdown = () => {
        if (Date.now() >= target) target = nextLocalMidnight();
        const remaining = Math.max(0, target - Date.now());
        const totalSeconds = Math.floor(remaining / 1000);
        const values = {
          hours: Math.floor(totalSeconds / 3600),
          minutes: Math.floor((totalSeconds % 3600) / 60),
          seconds: totalSeconds % 60
        };
        Object.entries(values).forEach(([key, value]) => {
          if (units[key]) units[key].textContent = String(value).padStart(2, '0');
        });
      };
      renderCountdown();
      countdown.countdownInterval = window.setInterval(renderCountdown, 1000);
    });
  };
  initCountdowns();
  document.addEventListener('shopify:section:load', (event) => initCountdowns(event.target));

  /* Cart drawer */
  const drawer = qs('[data-cart-drawer]');
  const overlay = qs('[data-cart-overlay]');
  const cartItems = qs('[data-cart-items]');
  const cartFooter = qs('[data-cart-footer]');

  const openCart = () => {
    drawer?.classList.add('is-open');
    overlay?.classList.add('is-open');
    drawer?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
  };
  const closeCart = () => {
    drawer?.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    drawer?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
  };

  qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', openCart));
  qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));
  overlay?.addEventListener('click', closeCart);
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') { closeCart(); closeNavigation(); } });

  const updateShippingMeter = (cart) => {
    const threshold = Number(document.body.dataset.freeShippingThreshold || 99) * 100;
    const progress = qs('[data-shipping-progress]');
    const copy = qs('[data-shipping-copy]');
    if (!progress || !copy) return;
    if (!Number.isFinite(threshold) || threshold <= 0) {
      progress.style.width = '100%';
      copy.textContent = 'Free shipping across the US';
      return;
    }
    const percent = Math.min(100, (cart.total_price / threshold) * 100);
    progress.style.width = `${percent}%`;
    const remaining = Math.max(0, threshold - cart.total_price);
    copy.textContent = remaining > 0 ? `Add ${money(remaining, cart.currency)} more for free shipping` : 'You unlocked free shipping';
  };

  const renderCart = (cart) => {
    qsa('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
    updateShippingMeter(cart);
    if (cartItems) {
      cartItems.innerHTML = cart.items.length ? cart.items.map((item) => `
        <div class="cart-line" data-line-key="${item.key}">
          ${item.image ? `<img src="${item.image}" alt="">` : ''}
          <div>
            <a href="${item.url}"><strong>${item.product_title}</strong></a>
            <small>${item.variant_title || ''}</small>
            <span>${money(item.final_line_price, cart.currency)}</span>
            <div class="cart-line__actions">
              <div class="quantity quantity--drawer">
                <button type="button" data-cart-quantity-change="-1" aria-label="Decrease quantity" ${item.quantity <= 1 ? 'disabled' : ''}>−</button>
                <input type="number" value="${item.quantity}" min="1" readonly aria-label="Item quantity">
                <button type="button" data-cart-quantity-change="1" aria-label="Increase quantity">+</button>
              </div>
              <button class="cart-line__remove" type="button" data-cart-remove="${item.key}">Remove</button>
            </div>
          </div>
        </div>`).join('') : '<div class="empty-state"><p>Your cart is ready for the right parts.</p><a class="button button--secondary" href="/collections/all">Start shopping</a></div>';
    }
    if (cartFooter) cartFooter.hidden = cart.item_count === 0;
    const total = qs('[data-cart-total]');
    if (total) total.textContent = money(cart.total_price, cart.currency);
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-add-to-cart]');
    if (!form) return;
    event.preventDefault();
    const button = qs('button[type="submit"]', form);
    if (button) { button.disabled = true; button.dataset.label = button.textContent; button.textContent = 'Adding…'; }
    try {
      const response = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to add this item');
      await fetchCart();
      openCart();
    } catch (error) {
      if (button) button.textContent = 'Please try again';
    } finally {
      setTimeout(() => { if (button) { button.disabled = false; button.textContent = button.dataset.label || 'Add to cart'; } }, 900);
    }
  });

  document.addEventListener('click', async (event) => {
    const quantityButton = event.target.closest('[data-cart-quantity-change]');
    if (quantityButton) {
      const line = quantityButton.closest('[data-line-key]');
      const control = quantityButton.closest('.quantity');
      const input = qs('input', control);
      const quantity = Math.max(1, Number(input?.value || 1) + Number(quantityButton.dataset.cartQuantityChange));
      qsa('button', control).forEach((button) => { button.disabled = true; });
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.dataset.lineKey, quantity })
        });
        if (!response.ok) throw new Error('Unable to update quantity');
        renderCart(await response.json());
      } catch (_) {
        qsa('button', control).forEach((button) => { button.disabled = false; });
      }
      return;
    }
    const removeButton = event.target.closest('[data-cart-remove]');
    if (!removeButton) return;
    removeButton.disabled = true;
    removeButton.textContent = 'Removing…';
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: removeButton.dataset.cartRemove, quantity: 0 })
      });
      if (!response.ok) throw new Error('Unable to remove this item');
      renderCart(await response.json());
    } catch (_) {
      removeButton.disabled = false;
      removeButton.textContent = 'Try again';
    }
  });

  fetchCart().catch(() => {});

  /* Promo codes and checkout transition */
  const purchaseTransition = qs('[data-purchase-transition]');
  const showPurchaseTransition = () => {
    if (!purchaseTransition) return;
    purchaseTransition.hidden = false;
    window.requestAnimationFrame(() => purchaseTransition.classList.add('is-active'));
  };
  const hidePurchaseTransition = () => {
    if (!purchaseTransition) return;
    purchaseTransition.classList.remove('is-active');
    purchaseTransition.hidden = true;
  };

  const applyDiscount = (container) => {
    const input = qs('input[name="discount"]', container);
    const error = qs('[data-discount-error]', container);
    const code = input?.value.trim();
    if (!code) {
      if (error) error.hidden = false;
      input?.focus();
      return;
    }
    if (error) error.hidden = true;
    showPurchaseTransition();
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/checkout`;
  };

  qsa('form[data-discount-form]').forEach((form) => form.addEventListener('submit', (event) => {
    event.preventDefault();
    applyDiscount(form);
  }));
  qsa('[data-discount-submit]').forEach((button) => button.addEventListener('click', () => {
    const container = button.closest('[data-discount-form]');
    if (container) applyDiscount(container);
  }));

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-checkout-submit], .shopify-payment-button')) showPurchaseTransition();
  });
  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-checkout-form]') && event.submitter?.name === 'checkout') showPurchaseTransition();
  });
  window.addEventListener('pageshow', hidePurchaseTransition);

  /* Product controls */
  qsa('[data-product-gallery]').forEach((gallery) => {
    const slides = qsa('[data-product-slide]', gallery);
    const thumbs = qsa('[data-product-thumb-index]', gallery);
    if (!slides.length) return;
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const showSlide = (requestedIndex) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      thumbs.forEach((thumb, index) => {
        const active = index === activeIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showSlide(Number(thumb.dataset.productThumbIndex))));
    qs('[data-gallery-previous]', gallery)?.addEventListener('click', () => showSlide(activeIndex - 1));
    qs('[data-gallery-next]', gallery)?.addEventListener('click', () => showSlide(activeIndex + 1));
    showSlide(activeIndex);
  });
  qsa('[data-qty-minus]').forEach((button) => button.addEventListener('click', () => {
    const input = button.parentElement.querySelector('input');
    input.value = Math.max(1, Number(input.value || 1) - 1);
  }));
  qsa('[data-qty-plus]').forEach((button) => button.addEventListener('click', () => {
    const input = button.parentElement.querySelector('input');
    input.value = Number(input.value || 1) + 1;
  }));

  /* Product description and vehicle fitment accordions */
  const initProductDescriptions = (context = document) => {
    const previews = [
      ...(context.matches?.('[data-description-preview]') ? [context] : []),
      ...qsa('[data-description-preview]', context)
    ];

    previews.forEach((preview) => {
      if (preview.dataset.descriptionReady === 'true') return;
      const content = qs('[data-description-content]', preview);
      const toggle = qs('[data-description-toggle]', preview);
      const label = qs('[data-description-toggle-label]', preview);
      const accordions = preview.closest('.product-accordions');
      const fitmentAccordion = qs('[data-fitment-accordion]', accordions);
      const fitmentContent = qs('[data-fitment-content]', fitmentAccordion);
      if (!content || !toggle) return;

      const fitmentHeading = qsa('h1,h2,h3,h4,h5,h6', content).find((heading) => {
        const text = heading.textContent.replace(/\s+/g, ' ').trim();
        return /^(vehicle\s+fitments?|fitment)$/i.test(text);
      });

      if (fitmentHeading && fitmentAccordion && fitmentContent) {
        let fitmentStart = fitmentHeading;
        while (fitmentStart.parentElement && fitmentStart.parentElement !== content) fitmentStart = fitmentStart.parentElement;
        let fitmentNode = fitmentStart.nextSibling;
        fitmentStart.remove();
        while (fitmentNode) {
          const nextNode = fitmentNode.nextSibling;
          fitmentContent.appendChild(fitmentNode);
          fitmentNode = nextNode;
        }
        if (fitmentContent.textContent.trim() || fitmentContent.querySelector('table,img')) fitmentAccordion.hidden = false;
      }

      preview.dataset.descriptionReady = 'true';
      const updateDescriptionOverflow = () => {
        const hasOverflow = content.scrollHeight > content.clientHeight + 2;
        preview.classList.toggle('has-overflow', hasOverflow);
        toggle.hidden = !hasOverflow;
      };
      updateDescriptionOverflow();
      window.requestAnimationFrame(updateDescriptionOverflow);
      toggle.addEventListener('click', () => {
        const expanded = preview.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', String(expanded));
        if (label) label.textContent = expanded ? 'Show less' : 'Show more';
      });
    });
  };
  initProductDescriptions();

  const markDescriptionBrandLogos = (context = document) => {
    qsa('.product-description-preview__content img, [data-fitment-content] img', context).forEach((image) => {
      const classify = () => {
        const hint = `${image.alt || ''} ${image.currentSrc || image.src || ''}`.toLowerCase();
        const bounds = image.getBoundingClientRect();
        const looksLikeBrandLogo = /nissan|brand|logo/.test(hint) || (bounds.width > 0 && bounds.width <= 180 && bounds.height > 0 && bounds.height <= 110);
        image.classList.toggle('product-description__brand-logo', looksLikeBrandLogo);
      };
      if (image.complete) classify();
      else image.addEventListener('load', classify, { once: true });
    });
  };
  markDescriptionBrandLogos();
  document.addEventListener('shopify:section:load', (event) => {
    initProductDescriptions(event.target);
    markDescriptionBrandLogos(event.target);
  });

  /* Collection controls */
  qs('[data-filter-toggle]')?.addEventListener('click', () => qs('[data-filters]')?.classList.toggle('is-open'));
  qs('[data-sort-select]')?.addEventListener('change', (event) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', event.target.value);
    window.location.href = url.toString();
  });

  /* Wishlist */
  const wishlistKey = 'swift-vanguard-wishlist';
  const readWishlist = () => { try { return JSON.parse(localStorage.getItem(wishlistKey)) || []; } catch (_) { return []; } };
  const paintWishlist = () => {
    const saved = readWishlist();
    qsa('[data-wishlist]').forEach((button) => button.classList.toggle('is-active', saved.includes(button.dataset.wishlist)));
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist]');
    if (!button) return;
    const id = button.dataset.wishlist;
    let saved = readWishlist();
    saved = saved.includes(id) ? saved.filter((item) => item !== id) : [...saved, id];
    try { localStorage.setItem(wishlistKey, JSON.stringify(saved)); } catch (_) {}
    paintWishlist();
  });
  paintWishlist();

  /* Cookie notice */
  const cookie = qs('[data-cookie-banner]');
  let accepted = false;
  try { accepted = localStorage.getItem('swift-vanguard-cookie') === 'accepted'; } catch (_) {}
  if (cookie && !accepted) cookie.hidden = false;
  qs('[data-cookie-accept]')?.addEventListener('click', () => {
    try { localStorage.setItem('swift-vanguard-cookie', 'accepted'); } catch (_) {}
    if (cookie) cookie.hidden = true;
  });
  qs('[data-cookie-dismiss]')?.addEventListener('click', () => { if (cookie) cookie.hidden = true; });

  /* Scroll reveal */
  const revealItems = qsa('[data-reveal]');
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
    }), { threshold: .12 });
    revealItems.forEach((item) => observer.observe(item));
  } else revealItems.forEach((item) => item.classList.add('is-visible'));
})();
